import React, { useEffect, useState } from "react";

import deepmerge from "deepmerge";
import { useTranslation } from "react-i18next";

export const UNDEFINED = "##UNDEFINED";
export const UNDEFINEDJSX: JSX.Element | string | number = (
  <span>{UNDEFINED}</span>
);

export type ResourceText =
  | string
  | number
  | (() => string | number | JSX.Element)
  | null
  | undefined;

export function printRes(
  jsx: ResourceText
): string | number | JSX.Element | null {
  if (!jsx) return null;
  if (typeof jsx === "string" || typeof jsx === "number") return jsx;
  return jsx();
}

// Adjusted the useTypedResource hook
export function useTypedResource<TResource>(
  path: string,
  defaultResource: TResource | null = null
): TResource {
  const { t, i18n } = useTranslation(); // Get i18n from useTranslation

  // Create a state to store the current language
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  const [resource, setResource] = useState<TResource>(() =>
    getResource(t, path, defaultResource)
  );

  useEffect(() => {
    // Function to update the resource based on the current language
    const updateResource = () => {
      setResource(getResource(t, path, defaultResource));
      setCurrentLanguage(i18n.language); // Update the current language
    };

    // Listen to language change events to update the resource
    i18n.on("languageChanged", updateResource);

    // Update resource immediately in case the language changes before the component mounts
    updateResource();

    // Cleanup by removing the event listener
    return () => {
      i18n.off("languageChanged", updateResource);
    };
  }, [path, defaultResource, t, i18n, currentLanguage]); // Updated dependencies

  return resource;
}

// Adjusted the getResource function
function getResource<TResource>(
  translate: (key: string, options?: any) => any,
  path: string,
  defaultResource: TResource | null
): TResource {
  const resource = translate(path, { returnObjects: true }) as TResource;
  return defaultResource ? deepmerge(defaultResource, resource) : resource;
}
